import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Tooltip,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import TableRowsIcon from '@mui/icons-material/TableRows'
import GridViewIcon from '@mui/icons-material/GridView'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import InventoryIcon from '@mui/icons-material/Inventory2'
import type { Item, ItemQuery } from '@iobiz/shared'
import { format, money, prixTTC, type CurrencyCode } from '@iobiz/shared'
import UploadIcon from '@mui/icons-material/UploadFile'
import QrCodeIcon from '@mui/icons-material/QrCode2'
import DownloadIcon from '@mui/icons-material/Download'
import { api } from '../api/client.js'
import { runCycle } from '../data/sync/engine.js'
import { ImportDialog } from '../features/customers/ImportDialog.js'
import { DataTable, EmptyState, type Column } from '../ui/DataTable.js'
import { ActionsDOnglet } from '../ui/ActionsDOnglet.js'
import { flattenCategories } from '../features/catalog/api.js'
import { useCategories, useItems, useStockLevels, useSuppliers } from '../data/hooks.js'
import { etatDeStock, type EtatStock, type RequeteArticles } from '../data/repositories/catalog.local.js'
import { ItemFormDialog } from '../features/catalog/ItemFormDialog.js'
import { StockBatchDialog } from '../features/catalog/StockBatchDialog.js'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import { ConfirmDialog } from '../ui/ConfirmDialog.js'
import { removeItem } from '../data/repositories/catalog.write.js'
import { useSessionStore } from '../stores/sessionStore.js'

/**
 * Catalogue — vue tableau (W-10) et vue cartes (W-11).
 *
 * Les deux vues partagent la même source et les mêmes filtres : basculer ne
 * relance aucune requête. La vue cartes sert au repérage visuel (usage
 * comptoir), la vue tableau à la comparaison ligne à ligne.
 */
export function ItemListPage({ kind }: { kind?: 'product' | 'service' }): JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const store = useSessionStore((s) => (slug ? s.storeBySlug(slug) : undefined))

  const [params, setParams] = useSearchParams()
  const query: RequeteArticles = useMemo(
    () => ({
      q: params.get('q') ?? undefined,
      categoryId: params.get('categoryId') ?? undefined,
      status: (params.get('status') as ItemQuery['status']) ?? undefined,
      // Posé par la carte « Produits en alerte » du tableau de bord, et
      // réglable ici : les deux passent par le même paramètre d'URL.
      stock: (params.get('stock') as RequeteArticles['stock']) ?? undefined,
      // Posé par l'avertissement « prix d'achat manquant » du tableau de bord.
      prixAchat: params.get('prixAchat') === 'manquant' ? 'manquant' : undefined,
      kind,
      sort: params.get('sort') ?? 'name',
      limit: Number(params.get('limit') ?? 50),
      offset: Number(params.get('offset') ?? 0),
    }),
    [params, kind],
  )

  // Lectures depuis la RÉPLIQUE : le catalogue reste consultable au comptoir
  // même sans réseau — c'est là qu'on en a le plus besoin.
  const page = useItems(store?.storeId, query)
  const categories = useCategories(store?.storeId)
  const stocks = useStockLevels(store?.storeId)

  /**
   * Produits sans code-barres sur la PAGE COURANTE.
   *
   * ⚠️ Le compte porte donc sur ce qui est affiché, pas sur tout le catalogue —
   * la liste est paginée côté serveur. Le bouton, lui, traite l'ensemble : le
   * nombre annoncé peut être inférieur au nombre attribué, jamais l'inverse.
   */
  const sansCodeBarres = (page?.data ?? []).filter(
    (i) => i.kind !== 'service' && !i.barcode,
  ).length

  /**
   * État de stock d'un article.
   *
   * ⚠️ La RUPTURE ne dépend pas du seuil : un article à zéro sans seuil défini
   * s'affichait en noir, comme si tout allait bien, alors que zéro est la seule
   * quantité qui empêche de vendre. La règle vit désormais dans le dépôt, avec
   * le filtre et le tri qui s'en servent — une seule définition, pas trois.
   */
  const etatStock = (i: Item): EtatStock => etatDeStock(i, stocks.get(i.id) ?? 0)
  const suppliers = useSuppliers(store?.storeId, { limit: 200 })?.data ?? []
  const loading = page === undefined
  const [draft, setDraft] = useState(query.q ?? '')
  const [view, setView] = useState<'table' | 'cards'>('table')
  const [formOpen, setFormOpen] = useState(false)
  const [selection, setSelection] = useState<string[]>([])
  const [importOuvert, setImportOuvert] = useState(false)
  const [attribution, setAttribution] = useState(false)
  /** Message de pied de barre : succès d'un traitement, ou échec à expliquer. */
  const [message, setMessage] = useState<{ texte: string; grave: boolean } | null>(null)
  const [confirmation, setConfirmation] = useState(false)
  const [saisieGroupee, setSaisieGroupee] = useState(false)
  const [menuActions, setMenuActions] = useState<HTMLElement | null>(null)

  const patch = useCallback(
    (changes: Record<string, string | undefined>) => {
      const next = new URLSearchParams(params)
      for (const [k, v] of Object.entries(changes)) {
        if (v === undefined || v === '') next.delete(k)
        else next.set(k, v)
      }
      if (!('offset' in changes)) next.delete('offset')
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      if (draft !== (query.q ?? '')) patch({ q: draft })
    }, 300)
    return () => clearTimeout(timer)
  }, [draft, query.q, patch])

  const currency = (store?.currencyCode ?? 'XAF') as CurrencyCode
  const price = (amount: number): string => format(money(amount, currency), 'fr-FR')

  const STATUS: Record<Item['status'], { label: string; color: 'success' | 'default' | 'warning' }> =
    {
      active: { label: 'Actif', color: 'success' },
      inactive: { label: 'Inactif', color: 'default' },
      discontinued: { label: 'Arrêté', color: 'warning' },
    }

  const columns: Column<Item>[] = [
    {
      key: 'name',
      label: 'Article',
      sortKey: 'name',
      render: (i) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar
            src={i.imageUrl ?? undefined}
            variant="rounded"
            sx={{ width: 32, height: 32, fontSize: 12, bgcolor: 'action.hover' }}
          >
            {i.name.slice(0, 2).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {i.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {i.kind === 'service' ? 'Service' : 'Produit'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    // Colonne à part entière, et TRIABLE : noyée en légende sous le nom, la
    // référence ne permettait ni de balayer une liste ni de la classer.
    {
      key: 'sku',
      label: 'Référence',
      sortKey: 'sku',
      render: (i) => (
        <Typography variant="body2" className="tabular" sx={{ color: 'text.secondary' }}>
          {i.sku ?? '—'}
        </Typography>
      ),
    },
    {
      /**
       * ⭐ Produit ou prestation : la distinction commande tout le reste — le
       * suivi de stock, le code-barres, et désormais la possibilité de prendre
       * rendez-vous. Elle ne se lisait qu'en ouvrant la fiche.
       *
       * La DURÉE accompagne le service quand elle est connue : c'est
       * l'information qui décide si la prestation peut être proposée à
       * l'agenda, et son absence se repère ainsi d'un coup d'œil sur la liste.
       */
      key: 'kind',
      label: 'Type',
      sortKey: 'kind',
      render: (i) =>
        i.kind === 'service' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip label="Service" size="small" color="secondary" variant="outlined" />
            {i.durationMinutes && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {i.durationMinutes} min
              </Typography>
            )}
          </Box>
        ) : (
          <Chip label="Produit" size="small" variant="outlined" />
        ),
    },
    {
      key: 'category',
      label: 'Catégorie',
      sortKey: 'categoryName',
      render: (i) => i.categoryName ?? '—',
    },
    {
      key: 'price',
      label: 'Prix de vente TTC',
      sortKey: 'salePrice',
      align: 'right',
      numeric: true,
      /**
       * ⭐ HT et TTC ensemble, TOUJOURS. Le catalogue est saisi hors taxes —
       * c'est la base de tous les calculs — mais le prix annoncé au client est
       * le TTC. N'afficher que l'un des deux oblige à convertir de tête.
       *
       * ⚠️ Le TTC s'affiche même sans taxe, où il égale le HT. Le masquer
       * donnait un tableau où la TVA n'apparaissait NULLE PART, et où rien ne
       * disait que l'article n'en portait simplement aucune.
       */
      /**
       * ⭐ Le TTC seul, car c'est le prix ANNONCÉ au client — celui qu'on
       * cherche en parcourant un catalogue. Le HT reste à portée de curseur :
       * il sert à la marge et à la facturation, pas au coup d'œil.
       */
      render: (i) => (
        <Tooltip title={`${price(i.salePrice)} hors taxes`} placement="left">
          <Typography variant="body2" className="tabular" sx={{ cursor: 'help' }}>
            {price(prixTTC(i.salePrice, i.taxRate))}
          </Typography>
        </Tooltip>
      ),
    },
    {
      key: 'tva',
      label: 'TVA',
      sortKey: 'taxRate',
      align: 'right',
      /* ⚠️ Un tiret plutôt qu'une case vide : « aucune taxe » est une
         information, et c'est celle qui explique un prix TTC égal au HT. */
      render: (i) => (
        <Typography variant="body2" className="tabular">
          {i.taxRate > 0 ? `${i.taxRate} %` : '—'}
        </Typography>
      ),
    },
    {
      key: 'stock',
      label: 'Stock',
      // ⭐ Triable : « qu'est-ce qui manque ? » est la question qu'on pose à
      // cette colonne, et y répondre à l'œil sur deux cents lignes est
      // exactement ce qu'un tri évite.
      sortKey: 'stock',
      align: 'right',
      numeric: true,
      render: (i) => {
        const etat = etatStock(i)
        if (etat === 'nonSuivi') {
          return (
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Non suivi
            </Typography>
          )
        }
        const q = stocks.get(i.id) ?? 0
        // Le CHIFFRE seul ne suffit pas : « 0 » et « 3 » se ressemblent en
        // balayant une colonne. Un mot dit ce qu'il faut faire.
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
            {etat === 'rupture' && (
              <Chip size="small" color="error" label="Rupture" sx={{ height: 20 }} />
            )}
            {etat === 'alerte' && (
              <Chip size="small" color="warning" label="Sous le seuil" sx={{ height: 20 }} />
            )}
            {/**
              * ⭐ Le chiffre NÉGATIF est un LIEN.
              *
              * Il ne se produit que quand des commandes ont réservé plus que
              * ce qui est en rayon (§ « la commande sort du stock, allant
              * jusqu'au négatif »). Le nombre EST la mesure de ce manque —
              * cliquer dessus doit mener droit aux commandes qui l'expliquent,
              * pas obliger à ressaisir le nom de l'article dans un autre écran.
              */}
            {q < 0 ? (
              <Typography
                component="button"
                variant="body2"
                className="tabular"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/s/${slug}/ventes?type=order&statut=validated&item=${i.id}`)
                }}
                sx={{
                  border: 0,
                  background: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: 'error.main',
                  fontWeight: 700,
                  minWidth: 24,
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                  '&:hover': { color: 'error.dark' },
                }}
              >
                {q}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                className="tabular"
                sx={{
                  color: etat === 'alerte' ? 'warning.main' : 'text.primary',
                  fontWeight: etat === 'ok' ? 400 : 700,
                  minWidth: 24,
                }}
              >
                {q}
              </Typography>
            )}
          </Box>
        )
      },
    },
    {
      key: 'status',
      label: 'Statut',
      sortKey: 'status',
      render: (i) => (
        <Chip size="small" label={STATUS[i.status].label} color={STATUS[i.status].color} />
      ),
    },
    {
      key: 'createdAt',
      label: 'Créé le',
      // ⭐ Triable : « qu'ai-je ajouté récemment ? » est la question qu'on pose
      // à cette colonne — un article saisi la veille se retrouve en un clic.
      sortKey: 'createdAt',
      align: 'right',
      render: (i) => (
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          {/* Le jour de la BOUTIQUE, comme partout : consulté de Paris le soir,
              un article créé à Bamako ne change pas de date. */}
          {i.createdAt
            ? new Date(i.createdAt).toLocaleDateString('fr-FR', {
                timeZone: store?.timezone ?? 'Africa/Douala',
              })
            : '—'}
        </Typography>
      ),
    },
  ]

  const filtresActifs = Boolean(
    query.q || query.categoryId || query.status || query.stock || query.prixAchat,
  )

  return (
    <>
      {/* ⭐ Les actions remontent DANS la barre d'onglets (voir
          `ActionsDOnglet`). Elles occupaient une seconde ligne entière, sous un
          titre « Catalogue » qui ne faisait que répéter l'onglet actif.

          ⭐ Seul « Nouveau » reste en façade : c'est l'action qu'on vient
          chercher. Le reste — importer, rattraper des codes-barres, saisir du
          stock — se fait une fois de temps en temps et n'a pas à occuper la
          barre en permanence. */}
      <ActionsDOnglet>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={view}
          onChange={(_, v: 'table' | 'cards' | null) => v && setView(v)}
        >
          <ToggleButton value="table" aria-label="Vue tableau">
            <TableRowsIcon sx={{ fontSize: 18 }} />
          </ToggleButton>
          <ToggleButton value="cards" aria-label="Vue cartes">
            <GridViewIcon sx={{ fontSize: 18 }} />
          </ToggleButton>
        </ToggleButtonGroup>

        <IconButton
          aria-label="Actions du catalogue"
          onClick={(e) => setMenuActions(e.currentTarget)}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}
        >
          <MoreVertIcon />
        </IconButton>

        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
          Nouveau
        </Button>

        <Menu
          anchorEl={menuActions}
          open={Boolean(menuActions)}
          onClose={() => setMenuActions(null)}
        >
          {/* Export et suppression n'apparaissent que sur SÉLECTION : un export
              permanent invite à sortir tout le catalogue par mégarde, et qui
              coche trois articles veut ces trois articles. */}
          {selection.length > 0 && (
            <MenuItem
              onClick={() => {
                setMenuActions(null)
                if (!store) return
                const search = new URLSearchParams({ ids: selection.join(',') })
                void api
                  .telecharger(
                    `/stores/${store.storeId}/items/export?${search.toString()}`,
                    `articles-${new Date().toISOString().slice(0, 10)}.csv`,
                  )
                  /* ⚠️ Ne JAMAIS avaler l'erreur ici. Hors ligne, l'export
                     échouait sans un mot : on cliquait, rien ne se passait,
                     et rien ne disait pourquoi. */
                  .catch((e: unknown) =>
                    setMessage({
                      texte: e instanceof Error ? e.message : 'Export impossible.',
                      grave: true,
                    }),
                  )
              }}
            >
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={`Exporter la sélection (${selection.length})`} />
            </MenuItem>
          )}

          {selection.length > 0 && (
            <MenuItem
              onClick={() => {
                setMenuActions(null)
                setConfirmation(true)
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary={`Supprimer la sélection (${selection.length})`} />
            </MenuItem>
          )}

          <MenuItem
            onClick={() => {
              setMenuActions(null)
              setImportOuvert(true)
            }}
          >
            <ListItemIcon>
              <UploadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Importer des articles" />
          </MenuItem>

          {/* ⭐ N'apparaît QUE s'il y a matière : une entrée qui ne ferait rien
              apprend à l'utilisateur à ne plus lire le menu. Les services sont
              exclus du compte — on ne scanne pas une prestation. */}
          {sansCodeBarres > 0 && (
            <MenuItem
              disabled={attribution}
              onClick={async () => {
                setMenuActions(null)
                if (!store) return
                setAttribution(true)
                try {
                  const r = await api.post<{ attribues: number }>(
                    `/stores/${store.storeId}/items/barcodes`,
                    undefined,
                    { sansAppareil: true },
                  )
                  setMessage({
                    texte:
                      r.attribues > 0
                        ? `${r.attribues} code${r.attribues > 1 ? 's' : ''}-barres attribué${r.attribues > 1 ? 's' : ''}.`
                        : 'Tous les produits en avaient déjà un.',
                    grave: false,
                  })
                  await runCycle(store.storeId)
                } catch (e) {
                  setMessage({
                    texte: e instanceof Error ? e.message : 'Attribution impossible.',
                    grave: true,
                  })
                } finally {
                  setAttribution(false)
                }
              }}
            >
              <ListItemIcon>
                <QrCodeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={`Codes-barres manquants (${sansCodeBarres})`} />
            </MenuItem>
          )}

          <MenuItem
            onClick={() => {
              setMenuActions(null)
              setSaisieGroupee(true)
            }}
          >
            <ListItemIcon>
              <InventoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Saisie de stock" />
          </MenuItem>
        </Menu>
      </ActionsDOnglet>

      {/* Le résultat de l'attribution doit se VOIR : un traitement silencieux
          laisse croire que rien ne s'est produit, et invite à recommencer. */}
      {message && (
        <Alert
          severity={message.grave ? 'warning' : 'info'}
          sx={{ mb: 4 }}
          onClose={() => setMessage(null)}
        >
          {message.texte}
        </Alert>
      )}

      <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Nom, référence, code-barres…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          {/* ⚠️ L'infobulle porte une règle qui SURPREND : filtrer sur
              « Informatique » ramène aussi les ordinateurs rangés dessous.
              Elle vivait en légende du titre de l'onglet Catégories, à un
              onglet de l'endroit où elle s'applique. */}
          <Tooltip title="Filtrer sur une catégorie inclut ses sous-catégories." placement="top">
            <TextField
              select
              label="Catégorie"
              size="small"
              value={query.categoryId ?? ''}
              onChange={(e) => patch({ categoryId: e.target.value })}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Toutes</MenuItem>
              {flattenCategories(categories).map((c) => (
                <MenuItem key={c.id} value={c.id} sx={{ pl: 2 + c.depth * 3 }}>
                  {c.label.trim()}
                </MenuItem>
              ))}
            </TextField>
          </Tooltip>
          <TextField
            select
            label="Statut"
            size="small"
            value={query.status ?? ''}
            onChange={(e) => patch({ status: e.target.value })}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">Tous</MenuItem>
            <MenuItem value="active">Actif</MenuItem>
            <MenuItem value="inactive">Inactif</MenuItem>
            <MenuItem value="discontinued">Arrêté</MenuItem>
          </TextField>
          {/* ⭐ Le filtre qu'ouvre la carte « Produits en alerte » du tableau
              de bord. Il est ici aussi, réglable à la main : arriver sur une
              liste filtrée sans voir par quoi enferme l'utilisateur dans un
              résultat qu'il ne sait pas défaire. */}
          <TextField
            select
            label="Stock"
            size="small"
            value={query.stock ?? ''}
            onChange={(e) => patch({ stock: e.target.value })}
            sx={{ minWidth: 175 }}
          >
            <MenuItem value="">Tous</MenuItem>
            <MenuItem value="attention">À réapprovisionner</MenuItem>
            <MenuItem value="rupture">En rupture</MenuItem>
            <MenuItem value="alerte">Sous le seuil</MenuItem>
            <MenuItem value="ok">En stock</MenuItem>
            <MenuItem value="nonSuivi">Non suivi</MenuItem>
          </TextField>
          {/* ⭐ Le filtre qu'ouvre l'avertissement du bénéfice net. Visible et
              réglable ici, pour la même raison que celui du stock : on doit
              voir par quoi la liste est réduite, et pouvoir le défaire. */}
          <TextField
            select
            label="Prix d’achat"
            size="small"
            value={query.prixAchat ?? ''}
            onChange={(e) => patch({ prixAchat: e.target.value })}
            sx={{ minWidth: 175 }}
          >
            <MenuItem value="">Tous</MenuItem>
            <MenuItem value="manquant">Manquant</MenuItem>
          </TextField>
          {filtresActifs && (
            <Button
              onClick={() => {
                setDraft('')
                setParams({}, { replace: true })
              }}
            >
              Réinitialiser
            </Button>
          )}
        </Box>
      </Paper>

      {view === 'cards' && !loading && (page?.data.length ?? 0) > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gap: 4,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
          }}
        >
          {page!.data.map((item) => {
            const etat = etatStock(item)
            const quantite = stocks.get(item.id) ?? 0
            return (
              <Card key={item.id} sx={{ borderRadius: 3 }}>
                <CardActionArea onClick={() => navigate(`/s/${slug}/catalogue/${item.id}`)}>
                  {/* ⭐ L'image OCCUPE toute la vignette. Réduite à 72 px au
                      centre d'un aplat gris, elle ne servait pas son objet :
                      on passe en vue cartes précisément pour reconnaître un
                      produit à sa photo, pas pour lire son nom en plus petit. */}
                  <Box
                    sx={{
                      position: 'relative',
                      height: 150,
                      bgcolor: 'action.hover',
                      display: 'grid',
                      placeItems: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    {item.imageUrl ? (
                      <Box
                        component="img"
                        src={item.imageUrl}
                        alt=""
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    ) : (
                      <InventoryIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                    )}
                    {/* L'alerte se pose SUR la photo : reléguée sous le prix,
                        elle se perdait dans une grille de vingt cartes. */}
                    {(etat === 'rupture' || etat === 'alerte') && (
                      <Chip
                        size="small"
                        color={etat === 'rupture' ? 'error' : 'warning'}
                        label={etat === 'rupture' ? 'Rupture' : 'Sous le seuil'}
                        sx={{ position: 'absolute', top: 8, left: 8 }}
                      />
                    )}
                  </Box>
                  <CardContent>
                    <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                      {item.categoryName ?? 'Sans catégorie'}
                    </Typography>
                    {/* Le TTC, comme dans le tableau : c'est le prix annoncé
                        au client, et deux vues du même catalogue ne peuvent pas
                        afficher deux prix différents. */}
                    <Typography className="tabular" sx={{ mt: 2, fontWeight: 600 }}>
                      {price(prixTTC(item.salePrice, item.taxRate))}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {item.taxRate > 0
                        ? `${price(item.salePrice)} HT · TVA ${item.taxRate} %`
                        : `${price(item.salePrice)} HT · sans TVA`}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        label={item.kind === 'service' ? 'Service' : STATUS[item.status].label}
                        color={item.kind === 'service' ? 'info' : STATUS[item.status].color}
                      />
                      {/* ⭐ Le stock manquait en vue cartes : c'est pourtant la
                          vue du comptoir, celle où l'on cherche si l'article
                          est disponible avant de l'annoncer au client. */}
                      <Chip
                        size="small"
                        variant="outlined"
                        // ⚠️ Sous zéro, « Rupture » ne suffit plus à dire ce
                        // qui se passe : le chiffre négatif EST la mesure de
                        // ce qui a été promis sans être en rayon (des
                        // commandes ont réservé plus que le disponible). Le
                        // clic vit sur la ligne du tableau, pas ici — une
                        // carte entière est déjà un bouton, et en imbriquer un
                        // second dedans se comporte mal d'un navigateur à
                        // l'autre.
                        label={
                          etat === 'nonSuivi'
                            ? 'Stock non suivi'
                            : quantite < 0
                              ? `${quantite} — commandes en attente`
                              : etat === 'rupture'
                                ? 'Rupture'
                                : `${quantite} en stock`
                        }
                        color={
                          etat === 'rupture' ? 'error' : etat === 'alerte' ? 'warning' : 'default'
                        }
                      />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            )
          })}
        </Box>
      ) : (
        <DataTable
          columns={columns}
          rows={page?.data ?? []}
          rowKey={(i) => i.id}
          /**
           * ⭐ Un LISERÉ à gauche, sans fond coloré.
           *
           * Le liseré se repère en balayant la page — le geste réel devant un
           * catalogue — sans repeindre la ligne : un fond teinté nuit à la
           * lecture du texte qu'il recouvre, et deux teintes côte à côte
           * transforment vite le tableau en signalisation routière.
           *
           * La couleur porte le NIVEAU, la pastille de la colonne Stock porte
           * le sens. Le liseré signale, il n'explique pas.
           *
           * ⚠️ Rien pour un article dont le stock n'est PAS suivi : lui coller
           * une alerte reviendrait à réclamer un réapprovisionnement pour une
           * prestation de service.
           */
          rowSx={(i) => {
            const etat = etatStock(i)
            if (etat === 'ok' || etat === 'nonSuivi') return undefined
            return {
              borderLeft: 4,
              borderLeftColor: etat === 'rupture' ? 'error.main' : 'warning.main',
            }
          }}
          loading={loading}
          total={page?.total ?? 0}
          limit={query.limit ?? 50}
          offset={query.offset ?? 0}
          sort={query.sort}
          onSortChange={(sort) => patch({ sort })}
          onPageChange={(offset, limit) =>
            patch({ offset: String(offset), limit: String(limit) })
          }
          onRowClick={(i) => navigate(`/s/${slug}/catalogue/${i.id}`)}
          selected={selection}
          onSelectionChange={setSelection}
          emptyState={
            <EmptyState
              icon={<InventoryIcon sx={{ fontSize: 48 }} />}
              /**
               * ⚠️ TOUS les filtres comptent, pas seulement la recherche.
               * Arriver du tableau de bord sur « À réapprovisionner » sans
               * résultat affichait « Catalogue vide — créez votre premier
               * produit » alors que le catalogue en compte deux cents : le
               * message accusait la base au lieu du filtre.
               */
              title={filtresActifs ? 'Aucun résultat' : 'Catalogue vide'}
              description={
                filtresActifs
                  ? 'Aucun article ne correspond aux critères retenus.'
                  : 'Créez votre premier produit ou service.'
              }
              action={
                filtresActifs ? (
                  <Button
                    onClick={() => {
                      setDraft('')
                      setParams({}, { replace: true })
                    }}
                  >
                    Réinitialiser les filtres
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setFormOpen(true)}
                  >
                    Nouveau
                  </Button>
                )
              }
            />
          }
        />
      )}

      {store && (
        <StockBatchDialog
          open={saisieGroupee}
          storeId={store.storeId}
          items={page?.data ?? []}
          onClose={() => setSaisieGroupee(false)}
          onDone={() => undefined}
        />
      )}

      {store && (
        <>
          <ImportDialog
            open={importOuvert}
            storeId={store.storeId}
            entite="items"
            titre="Importer des articles"
            colonnes="Type, Référence, Code-barres, Nom, Description, Unité, Prix d'achat, Prix de vente, Devise, Suivi stock, Seuil d'alerte, Statut"
            onClose={() => setImportOuvert(false)}
            onImported={() => {
              // L'import écrit sur le SERVEUR ; la liste lit la réplique. Sans
              // ce cycle, les articles importés restent invisibles.
              void runCycle(store.storeId)
            }}
          />

          <ConfirmDialog
          open={confirmation}
          title={`Supprimer ${selection.length} article${selection.length > 1 ? 's' : ''} ?`}
          message="Ils n’apparaîtront plus au catalogue. Les documents qui les mentionnent sont conservés."
          onConfirm={async () => {
            for (const id of selection) {
              const article = page?.data.find((i) => i.id === id)
              if (article) await removeItem(store.storeId, article)
            }
            setSelection([])
          }}
          onClose={() => setConfirmation(false)}
          />
        </>
      )}

      {store && (
        <ItemFormDialog
          open={formOpen}
          storeId={store.storeId}
          categories={categories}
          suppliers={suppliers}
          defaultKind={kind ?? 'product'}
          onClose={() => setFormOpen(false)}
          onSaved={() => undefined}
        />
      )}
    </>
  )
}
